/**
 * Properties Behavior — Data Interfaces
 *
 * Properties is the **public interface** of a template or scene object. It declares
 * named, typed values (color, size, label, etc.) that other behaviors consume via
 * `{name}` bindings, and optional **states** that conditionally override those values
 * with animated transitions.
 *
 * ## Binding syntax
 *
 * Any behavior data field can reference a property using `{name}` (preferred) or
 * legacy `$name` / `${name}` syntax. Inside `{}`, full JavaScript expression syntax
 * is supported:
 *
 * ```json
 * "color": "{color}"                              // simple lookup
 * "opacity": "{clamp(0, camera.distance / 10, 1)}" // expression with system values
 * "text": "Score: {score} / {max}"                 // inline interpolation
 * ```
 *
 * Built-in functions: `floor`, `ceil`, `round`, `abs`, `min`, `max`, `clamp`,
 * `lerp`, `map`, `smoothstep`, `sin`, `cos`, `random`, and more.
 *
 * ## System values
 *
 * System-provided values available in expressions (no declaration needed):
 *
 * | Expression        | Type   | Description                         |
 * |-------------------|--------|-------------------------------------|
 * | `time.elapsed`    | number | Seconds since play mode started     |
 * | `camera.distance` | number | Meters from this object to camera   |
 * | `camera.position` | Vec3   | Camera world position               |
 * | `self.position`   | Vec3   | This object's world position        |
 *
 * ## Inheritance
 *
 * Each property has an `inherits` flag (default `true`). When true, the nearest
 * ancestor's value takes precedence. When false, the local value wins. This
 * follows the CSS `@property { inherits }` model.
 *
 * ## States
 *
 * States conditionally override property values and transforms. They support two
 * types of transitions:
 *
 * - **Time-driven** (`duration`) — animates over a fixed duration, like CSS `transition`
 * - **Property-driven** (`driver`) — progress tracks a live value (including system values
 *   like `camera.distance`), like CSS `animation-timeline: scroll()`
 *
 * Pseudo-states (`:hover`, `:active`, `:selected`) auto-trigger from pointer events.
 * Multiple states stack by priority: `:active` (40) > `:selected` (30) > `:hover` (20) > custom (10).
 * Per-property, the highest-priority state providing a value wins.
 *
 * ## Value resolution order
 *
 * `getValue(name)` resolves through:
 * 1. Transition value (mid-animation interpolated value)
 * 2. Eligible states by priority — for each: ancestor state value → runtime override → local state value
 * 3. Ancestor base value (outermost `initialValue` with `inherits: true`)
 * 4. Runtime override (from `setValue()`)
 * 5. Local `initialValue`
 *
 * Inheritance uses walk-on-read (per-call ancestor traversal, no caching). This handles
 * reparenting naturally but is O(depth) per call.
 *
 * ## Interpolation
 *
 * During transitions, values are interpolated by type:
 * - **number**: linear interpolation
 * - **color**: component-wise linear RGB
 * - **vec3/vec4**: component-wise linear
 * - **boolean/string**: discrete snap at 50% progress
 *
 * Interrupted transitions capture the current interpolated value as the new "from".
 *
 * ## Expressions
 *
 * Expressions inside `{}` are compiled via `new Function` with a flat scope containing
 * resolved properties, system values, and built-in math functions. Single `{expr}` is
 * type-preserving; inline `"text {expr}"` always produces a string. Literal `{` is
 * escaped as `{{`.
 *
 * See `docs/canopy/` for the formal Canopy specification.
 */

import { BehaviorData } from '../behaviordata';

// ============================================================================
// UNITS
// ============================================================================

/**
 * Length unit a `number` / `vec3` declaration's numbers are written in.
 *
 * The tagged numbers (`value`, `min`, `max`, `step`, and any state override of
 * the property) live in **declared space**; `getValue()` returns **canonical
 * metres**. So `{ units: 'Feet', value: 3 }` reads back as `0.9144` and a
 * `step: 1` moves the scene one foot. Absent `units` means the numbers are
 * already canonical — identical to the pre-units behaviour. Only meaningful on
 * `number` and `vec3`; other types ignore it.
 *
 * See docs/canopy/CANOPY_UNITS.md.
 */
export type Units = 'Meters' | 'Feet';

// ============================================================================
// TRANSITION CONFIG
// ============================================================================

/**
 * Easing functions for property transitions.
 *
 * Every easing IS a cubic bezier — the canonical form is
 * `cubic-bezier(x1, y1, x2, y2)` (CSS syntax; x values in [0,1]) and the
 * named curves are aliases for fixed control points (the table lives in
 * CANOPY_CORE §9 and `EASING_CONTROL_POINTS`). This keeps the vocabulary
 * KHR-aligned: any easing can ride a standard `pointer/interpolate` op
 * unchanged, and a conforming client needs one bezier evaluator plus the
 * alias table — no per-curve formulas.
 *
 * `snap` is the near-instant-start premium arrival; `back` settles through
 * a single ≈5.7% overshoot; `anticipate` pulls back slightly before
 * launching (transform consumers extrapolate through over/undershoot;
 * color/opacity consumers clamp). Curves that a single bezier arc cannot
 * express (bounce, ringing springs) are not easings — author them as
 * animation-driven transitions instead. Unknown names degrade to easeInOut
 * at runtime — loaded data never crashes the lookup.
 */
export type EasingFunction =
    | 'linear' | 'easeIn' | 'easeOut' | 'easeInOut'
    | 'snap' | 'back' | 'anticipate'
    | `cubic-bezier(${string})`;

/**
 * Time-driven transition — interpolates over a fixed duration.
 *
 * CSS analogy: `transition: all 0.3s ease-out`
 *
 * ## Example
 *
 * ```json
 * { "duration": 0.3, "easing": "easeOut" }
 * ```
 */
export interface TimeTransition {
  /**
   * Transition duration in seconds
   * @min 0
   */
  duration: number;

  /**
   * Easing function
   * @default "easeInOut"
   */
  easing?: EasingFunction;

  /**
   * Delay before transition starts, in seconds
   * @default 0
   * @min 0
   */
  delay?: number;
}

/**
 * Property-driven transition — progress tracks a live property value
 * instead of elapsed time. Reversible and continuous.
 *
 * The `driver` can be:
 * - A declared property name (e.g. `"scrollProgress"`)
 * - A system value expression (e.g. `"camera.distance"`, `"time.elapsed"`)
 *
 * CSS analogy: `animation-timeline: scroll()` + `animation-range`
 *
 * ## Examples
 *
 * Fade in as camera approaches (6m → 0m):
 * ```json
 * { "driver": "camera.distance", "range": [6, 0], "easing": "easeOut" }
 * ```
 *
 * Fade in over the first 5 seconds of play:
 * ```json
 * { "driver": "time.elapsed", "range": [0, 5] }
 * ```
 *
 * Track a user-declared property:
 * ```json
 * { "driver": "scrollProgress", "range": [0, 1] }
 * ```
 */
export interface PropertyTransition {
  /**
   * Property name or system value expression that drives interpolation progress.
   * Resolved via property lookup (local → ancestor walk), then system values
   * (`camera.distance`, `time.elapsed`, etc.). Must resolve to a number.
   */
  driver: string;

  /**
   * Maps driver value range to [0, 1] progress.
   * - `range[0]` → progress 0 (base/initial values)
   * - `range[1]` → progress 1 (full state values)
   * - Values outside range are clamped
   * - Inverted ranges are valid (e.g. `[10, 0]` for "closer = more")
   * @default [0, 1]
   */
  range?: [number, number];

  /**
   * Easing applied to the mapped progress.
   * @default "linear"
   */
  easing?: EasingFunction;
}

/**
 * Transition config — either time-driven or property-driven.
 *
 * Discriminant: presence of `driver` field (PropertyTransition)
 * vs `duration` field (TimeTransition).
 *
 * Lives on StateDefinition to control how property values animate
 * when the state becomes active.
 */
export type TransitionConfig = TimeTransition | PropertyTransition;

/** Type guard: true if the transition is driven by a property value */
export function isPropertyTransition(t: TransitionConfig): t is PropertyTransition {
  return 'driver' in t;
}

/** Type guard: true if the transition is time-driven */
export function isTimeTransition(t: TransitionConfig): t is TimeTransition {
  return 'duration' in t;
}

// ============================================================================
// PROPERTIES BEHAVIOR
// ============================================================================

/**
 * Properties — the public interface of a template or scene object.
 *
 * Declares named, typed properties with initial values, optional external sheets,
 * and named states for conditional overrides with transitions. A declaration can
 * also carry `variants` (named preset values, shown as a swatch row in editors)
 * and a default `transition` that animates programmatic value changes.
 *
 * ## Binding syntax
 *
 * Other behaviors reference properties using `{name}` in their data fields:
 * ```json
 * { "@type": "Renderer", "materials": [{ "baseColorFactor": "{color}" }] }
 * ```
 *
 * Expressions are supported inside `{}`:
 * ```json
 * { "@type": "Text", "text": "Distance: {floor(camera.distance)} meters" }
 * ```
 *
 * ## Complete example
 *
 * ```json
 * {
 *   "@type": "Properties",
 *   "properties": [
 *     { "name": "color", "type": "color", "value": "#FF0000", "label": "Primary",
 *       "variants": [ { "name": "Red", "value": "#FF0000" }, { "name": "Teal", "value": "#00AA88" } ],
 *       "transition": { "duration": 0.3, "easing": "easeOut" } },
 *     { "name": "fontSize", "type": "number", "value": 16, "min": 8, "max": 48 }
 *   ],
 *   "states": {
 *     ":hover": {
 *       "values": { "color": "#00FF00" },
 *       "transition": { "duration": 0.3, "easing": "easeOut" }
 *     },
 *     "nearby": {
 *       "values": { "color": "#0000FF", "fontSize": 24 },
 *       "transition": { "driver": "camera.distance", "range": [5, 1] }
 *     }
 *   },
 *   "initialState": "nearby"
 * }
 * ```
 */
export interface PropertiesData extends BehaviorData {
  '@type': 'Properties';

  /**
   * Property declarations — name, type, value, and optional constraints.
   *
   * Each property is available to descendant behaviors via `{name}` bindings.
   * The `value` is the default when no state is active and no ancestor
   * overrides the property.
   *
   * @default []
   */
  properties: PropertyDeclaration[];

  /**
   * The object's own pose at the **base** level — no state involved.
   *
   * Same absolute-per-component semantics as a state's
   * {@link TransformOverride}: an omitted field keeps the authored value.
   * The difference is when it applies — a state's transform applies while
   * that state is active, this one applies whenever no state overrides it,
   * and it is the baseline a state reverts to.
   *
   * Its reason to exist is bound components: `[1, "{stageHeight}", 1]` makes
   * the pose a function of a declared property, re-applied whenever that
   * value changes. Without it a property can only drive fields on behaviors,
   * never the transform of the object carrying them.
   *
   * ```json
   * { "@type": "Properties", "properties": [], "transform": { "scale": [1, "{stageHeight}", 1] } }
   * ```
   */
  transform?: TransformOverride;

  /**
   * External property sheet URLs. Each URL points to a JSON file — either a
   * bare `PropertyDeclaration[]` (v0) or a {@link PropertySheet} envelope.
   * Declarations from sheets are merged with inline `properties` — inline
   * declarations override sheet declarations at the same name.
   *
   * Order is meaningful: among sheets, the **first** to declare a name supplies
   * it.
   *
   * **This is also how a style is applied.** A style asset *is* a sheet, so
   * applying one is storing its URL here and swapping styles is replacing that
   * entry — the applied style is the stored reference, with nothing inferred
   * from values. By editor convention the entry at index 0 is the style slot
   * (highest sheet precedence); further entries are ordinary declaration
   * sheets or layered partial styles. See `docs/STYLES.md`.
   */
  sheets?: string[];

  /**
   * Named states — conditional property value overrides with optional
   * transform overrides and transitions.
   *
   * **Pseudo-states** (names starting with `:`) auto-trigger from events:
   * - `:hover` — pointer enters/exits the object
   * - `:active` — pointer is pressed on the object
   * - `:selected` — object is selected via click-to-select
   *
   * **Custom states** are activated programmatically via `setState()` or `initialState`.
   *
   * States stack by priority: `:active` (40) > `:selected` (30) > `:hover` (20) > custom (10).
   * Per-property, the highest-priority state providing a value wins.
   */
  states?: Record<string, StateDefinition>;

  /**
   * State name to activate when the scene enters play mode.
   * Must reference a key in `states`. Useful for property-driven transitions
   * that should be active from the start (e.g. camera-distance effects).
   */
  initialState?: string;

  /**
   * When `true`, this object OWNS its state group and is a *group boundary*. Its
   * state list is independent of any group above it, so an outer group's state
   * coordination (add / rename / delete / switch fan-out) stops at this object and
   * does not reach into its subtree. This is what lets, e.g., a model keep its own
   * "separated" states while sitting inside an ancestor group, the two combining
   * independently.
   *
   * **Runtime-load-bearing.** `trace/setState(target, s)` is a SCOPED runtime op:
   * it walks the target's subtree and sets every participating descendant's slot,
   * pruning at `stateGroup` boundaries (see docs/canopy/STATE_SCOPE_SPEC.md §3 and
   * core/canopy/stateScope.ts). Runtimes MUST honor this marker (a boundary and
   * its subtree are excluded from an ancestor's scoped set); exporters MUST
   * preserve it. A plain boolean marker — the group is identified by the object,
   * not a separate axis name. See docs-internal/STATE_GROUP_BOUNDARIES.md.
   */
  stateGroup?: boolean;
}

// ============================================================================
// STATES
// ============================================================================

/**
 * A named state — conditional property value overrides applied when active.
 *
 * States can override property values, the owning object's transform, and its
 * active (enabled) status. Transitions control how values animate when the
 * state activates.
 *
 * ## Time-driven example
 *
 * ```json
 * {
 *   "values": { "color": "#00FF00", "opacity": 0.8 },
 *   "transform": { "scale": [1.1, 1.1, 1.1] },
 *   "transition": { "duration": 0.3, "easing": "easeOut" }
 * }
 * ```
 *
 * ## Property-driven example (camera proximity)
 *
 * ```json
 * {
 *   "values": { "opacity": 1 },
 *   "transition": { "driver": "camera.distance", "range": [5, 1], "easing": "easeOut" }
 * }
 * ```
 *
 * ## Hide / show example
 *
 * ```json
 * {
 *   "values": {},
 *   "active": false,
 *   "transition": { "duration": 0.3 }
 * }
 * ```
 *
 * ## Enter-from offset
 *
 * `enterFrom` is consumed when the object transitions from inactive → active.
 * The runtime snaps to `(target pose + enterFrom)` and animates to the target,
 * giving objects a "fly in from" starting pose.
 */
export interface StateDefinition {
  /**
   * Property value overrides. Keys are property names from declarations.
   * Values must match the property's declared type.
   * Properties not listed keep their value (or ancestor override).
   */
  values: Record<string, PropertyValue>;

  /**
   * Transform overrides applied to the owning SceneObject when the state is active.
   * Interpolated alongside property values during transitions.
   */
  transform?: TransformOverride;

  /**
   * Active (enabled) override. When the top eligible state declares
   * `active: false`, the owning object is hidden after the state's transition
   * completes. `active: true` shows it immediately so the object is visible
   * during entry animation. Runtime only: the object's authored `active` is
   * unchanged. Omitted means no change.
   */
  active?: boolean;

  /**
   * Entry pose for objects becoming active (inactive → active). When the runtime
   * transitions an object from `active:false` to `active:true` with `enterFrom`
   * set, the "from" pose for transform interpolation is this **absolute local
   * pose** instead of the live pose (omitted fields fall back to the target pose).
   * Same absolute semantics as `transform`.
   */
  enterFrom?: TransformOverride;

  /**
   * Transition config for entering this state.
   *
   * - **Time-driven** (`{ duration, easing?, delay? }`): animates over a fixed duration
   * - **Property-driven** (`{ driver, range?, easing? }`): progress tracks a live value
   *
   * Without a transition, values snap immediately.
   */
  transition?: TransitionConfig;
}

/**
 * Transform override for states — an **absolute local pose** (relative to the
 * object's parent) applied when the state is active. Replaces wholesale, CSS-style
 * (`:hover { transform }` replaces, it does not add to a base). Every field is
 * optional; an **omitted field falls back to the authored value** for that
 * component ("no change by omission"). See ABSOLUTE_STATE_TRANSFORMS.md.
 *
 * ## Example
 *
 * ```json
 * { "position": [0, 0.5, 0], "scale": [1.2, 1.2, 1.2] }
 * ```
 */
export interface TransformOverride {
  /** Absolute local position `[x, y, z]` (relative to parent). Omit → authored position. */
  position?: [TransformComponent, TransformComponent, TransformComponent];
  /** Absolute local rotation as a unit quaternion `[x, y, z, w]`. Omit → authored rotation. Interpolated with slerp. */
  rotation?: [TransformComponent, TransformComponent, TransformComponent, TransformComponent];
  /** Absolute local scale `[x, y, z]` (`[0,0,0]` = collapse to zero size). Omit → authored scale. Use `active: false` to fully disable the object. */
  scale?: [TransformComponent, TransformComponent, TransformComponent];
}

/**
 * One component of a transform tuple: a literal number, or a `{binding}` /
 * `$name` string resolved through the ordinary property cascade before the
 * pose is computed. A binding that resolves to nothing falls back to the
 * authored value for that component rather than writing NaN.
 */
export type TransformComponent = number | string;

/**
 * A transform tuple as plain numbers, or `undefined` when any component is a
 * binding.
 *
 * Pose math — composing, reframing, comparing — cannot operate on a component
 * whose value isn't known until resolution. Callers that do that math use this
 * to ask the question explicitly instead of assuming numbers.
 */
export function numericTuple(
  tuple: readonly TransformComponent[] | undefined,
): number[] | undefined {
  if (!tuple) return undefined;
  return tuple.every(c => typeof c === 'number') ? (tuple as number[]) : undefined;
}

/**
 * A fully-specified numeric pose: every component present, none of them bound.
 * What pose math operates on, as opposed to what a transform may be authored
 * as.
 */
export interface ResolvedPose {
  position: [number, number, number];
  rotation: [number, number, number, number];
  scale: [number, number, number];
}

/** Whether any component of the override is a binding rather than a number. */
export function hasBoundComponent(transform: TransformOverride | undefined): boolean {
  if (!transform) return false;
  return [transform.position, transform.rotation, transform.scale]
    .some(tuple => tuple && !numericTuple(tuple));
}

// PropertyValue type — matches the union of all property value types.
// Re-declared here to avoid circular imports (tracebehavior.ts defines it).
type PropertyValue = string | number | boolean | [number, number, number] | [number, number, number, number];


// ============================================================================
// PROPERTY DECLARATIONS
// ============================================================================

/**
 * Property declaration — defines a property's name, type, initial value, and constraints.
 *
 * Like CSS `@property`, the initial value is part of the declaration. States override
 * the initial value when active. Other behaviors consume the property via `{name}` bindings.
 *
 * ## Supported types
 *
 * | Type      | value example             | Notes                        |
 * |-----------|----------------------------------|------------------------------|
 * | `string`  | `"Hello"`                        |                              |
 * | `number`  | `16`                             | Optional min/max/step/units  |
 * | `boolean` | `true`                           |                              |
 * | `color`   | `"#FF0000"`                      | Hex color string             |
 * | `vec3`    | `[0, 1, 0]`                      | 3D vector `[x, y, z]`; optional units |
 * | `vec4`    | `[0, 0, 0, 1]`                   | 4D vector `[x, y, z, w]`    |
 */
export type PropertyDeclaration =
  | StringDeclaration
  | NumberDeclaration
  | BooleanDeclaration
  | Vector3Declaration
  | Vector4Declaration
  | ColorDeclaration;

/** Base interface for all property declarations. */
interface BaseDeclaration {
  /** Property identifier — used in `{name}` bindings and state value overrides */
  name: string;

  /** Display label for editor UI. Makes the property discoverable by AutoUI. */
  label?: string;

  /**
   * Keep the property out of generated controls (AutoUI). It stays declared,
   * bindable and editable in an inspector — this is for values that are
   * *driven* rather than adjusted: a page's deck height set by page
   * selection, a compiled offset. Runtimes ignore it.
   * @default false
   */
  hidden?: boolean;

  /**
   * Whether this property inherits its value from an ancestor.
   *
   * - `true` (default): "I inherit from my parent" — ancestor value wins.
   * - `false`: "I don't inherit" — local value wins, ancestor walk skipped.
   *
   * Like CSS `@property { inherits }`, but per-declaration rather than global.
   * @default true
   */
  inherits?: boolean;

  /**
   * Default transition for programmatic value changes (CSS: `transition: color .3s`).
   * Precedence: an initiator's explicit transition (a state's `transition`, a
   * graph edge's config, a `setValue` argument) wins; this is the fallback; no
   * transition anywhere = snap. Never consulted for instant (snap) recomputes.
   * Time-driven only by type.
   */
  transition?: TimeTransition;
}

/**
 * Semantic hint for string properties whose value is an asset URL.
 *
 * - `material` — a material GLB url, e.g. bound to `Renderer.materials[].src`
 * - `animation-clip` — an animation GLB url, e.g. bound to
 *   `AnimationSequence.states.{enter,main,exit}.src`. Lets a state drive which
 *   clip plays (enter/while-active/exit) by overriding the bound src string.
 *
 * The runtime ignores this field; it exists for editors (render an asset
 * picker instead of a text input) and the publish pipeline (values —
 * including state overrides — must be hosted URLs at publish time, and are
 * prefetched by consumers like Renderer / AnimationSequence).
 */
export type StringPropertyKind = 'material' | 'animation-clip';

/**
 * String property declaration
 *
 * ```json
 * { "name": "title", "type": "string", "value": "Hello World", "label": "Title" }
 * ```
 *
 * Asset-holding strings declare a `kind` so tooling knows the value is a URL:
 *
 * ```json
 * { "name": "material", "type": "string", "kind": "material", "value": "https://…/gold.glb" }
 * ```
 */
export interface StringDeclaration extends BaseDeclaration {
  type: 'string';
  value: string;
  /** Optional asset-kind hint — see {@link StringPropertyKind} */
  kind?: StringPropertyKind;
}

/**
 * Numeric property declaration with optional constraints for editor UI.
 *
 * ```json
 * { "name": "fontSize", "type": "number", "value": 16, "min": 8, "max": 48, "step": 1 }
 * ```
 */
export interface NumberDeclaration extends BaseDeclaration {
  type: 'number';
  value: number;
  /**
   * Length unit of `value`/`min`/`max`/`step` and of this property's state
   * values — declared space; `getValue()` returns canonical metres.
   * See docs/canopy/CANOPY_UNITS.md.
   */
  units?: Units;
  /** Minimum value (for slider/validation) */
  min?: number;
  /** Maximum value (for slider/validation) */
  max?: number;
  /** Step increment (for slider/input) */
  step?: number;
  /** UI hint for how AutoUI should render this property */
  uiHint?: 'slider' | 'input';
}

/**
 * Boolean property declaration
 *
 * ```json
 * { "name": "visible", "type": "boolean", "value": true }
 * ```
 */
export interface BooleanDeclaration extends BaseDeclaration {
  type: 'boolean';
  value: boolean;
}

/**
 * 3D vector property declaration
 *
 * ```json
 * { "name": "offset", "type": "vec3", "value": [0, 1, 0] }
 * ```
 */
export interface Vector3Declaration extends BaseDeclaration {
  type: 'vec3';
  value: [number, number, number];
  /**
   * Length unit of each component — declared space; `getValue()` returns
   * canonical metres. See docs/canopy/CANOPY_UNITS.md.
   */
  units?: Units;
}

/**
 * 4D vector property declaration for generic 4-component data (RGBA, weights, etc.)
 *
 * Transitions use component-wise linear interpolation. For quaternion rotation,
 * use `TransformOverride.rotation` on a state instead — it uses proper slerp.
 *
 * ```json
 * { "name": "tint", "type": "vec4", "value": [1, 0.5, 0, 1] }
 * ```
 */
export interface Vector4Declaration extends BaseDeclaration {
  type: 'vec4';
  value: [number, number, number, number];
}

/**
 * Color property declaration (hex string)
 *
 * ```json
 * { "name": "color", "type": "color", "value": "#FF0000", "label": "Primary Color" }
 * ```
 */
export interface ColorDeclaration extends BaseDeclaration {
  type: 'color';
  value: string;
  /**
   * Named candidate values ("variants") for this property, rendered by editors
   * as a swatch row. Authoring metadata: runtime resolution ignores this field,
   * and variant names are display-only — consumers bake the VALUE, never
   * reference a variant by name. Values are 6-digit hex (#rrggbb).
   */
  variants?: { name: string; value: string }[];
}

/**
 * The file format an external sheet URL points at.
 *
 * A sheet is the portable tier: declarations that travel between scenes.
 * `PropertiesData.sheets` lists the URLs, and this envelope is the one accepted
 * form.
 *
 * **A style is a sheet** — there is no separate style format. A style asset is
 * a sheet whose declarations carry the themed values, and applying it is
 * referencing it from `PropertiesData.sheets`. What used to be a named bundle
 * of `values` is just this file's `declarations`, which is why the styling
 * system needs nothing here beyond what sheets already had.
 *
 * ```json
 * {
 *   "version": 1,
 *   "label": "Chrome",
 *   "declarations": [
 *     { "name": "structureSrc", "type": "string", "kind": "material", "value": "https://…/chrome.glb" },
 *     { "name": "structureColor", "type": "color", "value": "#DDDDE0" }
 *   ]
 * }
 * ```
 */
export interface PropertySheet {
  /**
   * Envelope version. A reader that does not know this version must reject the
   * sheet rather than parse it partially — a half-understood sheet resolves to
   * wrong values instead of an error.
   */
  version: number;

  /**
   * Human-facing name, for sheets that are browsed rather than merely
   * referenced — a style library's swatch row needs a label per entry.
   *
   * Optional because a sheet stored as a CMS asset carries its display name in
   * the asset's own metadata; this is the same field for sheets that live as
   * files, so one shape describes both. Never resolved, never matched against:
   * the applied style is the stored URL, not this string.
   */
  label?: string;

  /** Declarations this sheet contributes. */
  declarations?: PropertyDeclaration[];
}

/** The envelope version this build writes. */
export const PROPERTY_SHEET_VERSION = 1;

/**
 * Parse a sheet file into the envelope.
 *
 * Returns `null` for anything unparseable or carrying a version this build
 * doesn't know, so callers can warn and skip one sheet rather than failing the
 * whole object. A half-understood sheet would resolve to wrong values instead
 * of an error, which is worse than not loading.
 *
 * Two accepted forms:
 * - the versioned envelope, which is what every writer emits;
 * - a bare `PropertyDeclaration[]`, read as v0. No *tool* ever wrote that
 *   shape, but the published spec (CANOPY_CORE §3.4) documented it as *the*
 *   sheet format, so hand-authored sheets legitimately exist in that form —
 *   the spec is a writer too. Upgraded to the envelope on read; three lines
 *   of compatibility versus silently breaking spec-conformant content.
 */
export function parsePropertySheet(raw: unknown): PropertySheet | null {
  if (Array.isArray(raw)) {
    // Element shape is deliberately NOT checked here — this function decides
    // the envelope, and the loader (`Properties._parseSheet`) validates the
    // contents of both forms in one place.
    return { version: PROPERTY_SHEET_VERSION, declarations: raw as PropertyDeclaration[] };
  }
  if (!raw || typeof raw !== 'object') return null;

  const envelope = raw as Partial<PropertySheet> & { styles?: unknown };
  if (envelope.version !== PROPERTY_SHEET_VERSION) return null;

  return {
    version: envelope.version,
    ...(typeof envelope.label === 'string' ? { label: envelope.label } : {}),
    declarations: Array.isArray(envelope.declarations) ? envelope.declarations : undefined,
  };
}

/**
 * Whether a sheet carries a `styles` block — the named-value-bundle tier that
 * predates styles-as-sheets.
 *
 * Not a compatibility path: nothing reads that block any more, and this
 * function exists so the loader can *say so* rather than hand back a sheet that
 * silently contributes nothing. A style asset written in the old shape has all
 * of its content in `styles`, so parsing it as an empty sheet is the difference
 * between "your theme is blank" and a legible reason why.
 */
export function hasLegacyStyleBlock(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const styles = (raw as { styles?: unknown }).styles;
  return !!styles && typeof styles === 'object';
}
